import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ArrowRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { money } from "@/lib/semanas";
import { usePublicaciones, PubSelect, EmptyPubs, Var } from "@/components/control/cneShared";

export function CneComparar() {
  const { data: pubs, isLoading } = usePublicaciones();
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [onlyChanged, setOnlyChanged] = useState(true);
  const aId = a || pubs?.[1]?.id || pubs?.[0]?.id;
  const bId = b || pubs?.[0]?.id;
  const { data: rows, isLoading: lr } = useQuery({
    queryKey: ["cne", "comparar", aId, bId],
    queryFn: async () => (await api.get("/control-semanal/cne/comparar", { params: { a: aId, b: bId } })).data.items,
    enabled: !!aId && !!bId,
  });
  const shown = useMemo(() => (rows || []).filter((r) => !onlyChanged || r.var_kg !== 0), [rows, onlyChanged]);
  const stats = useMemo(() => {
    const v = (rows || []).filter((r) => r.var_kg != null);
    return { sube: v.filter((r) => r.var_kg > 0).length, baja: v.filter((r) => r.var_kg < 0).length, igual: v.filter((r) => r.var_kg === 0).length };
  }, [rows]);

  if (isLoading) return <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>;
  if (!pubs?.length) return <EmptyPubs />;
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <PubSelect pubs={pubs} value={aId} onChange={setA} testid="cne-cmp-a" />
        <ArrowRight className="w-4 h-4 text-slate-400" />
        <PubSelect pubs={pubs} value={bId} onChange={setB} testid="cne-cmp-b" />
        <label className="flex items-center gap-2 text-sm text-slate-600 ml-auto">
          <Switch checked={onlyChanged} onCheckedChange={setOnlyChanged} data-testid="cne-cmp-only-changed" />Solo con variación
        </label>
      </div>
      <div className="grid grid-cols-3 gap-3" data-testid="cne-cmp-stats">
        <div className="bg-white rounded-2xl border border-slate-200/70 p-4"><p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Suben</p><p className="text-2xl font-extrabold text-red-600">{stats.sube}</p></div>
        <div className="bg-white rounded-2xl border border-slate-200/70 p-4"><p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Bajan</p><p className="text-2xl font-extrabold text-emerald-600">{stats.baja}</p></div>
        <div className="bg-white rounded-2xl border border-slate-200/70 p-4"><p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Sin cambio</p><p className="text-2xl font-extrabold text-slate-700">{stats.igual}</p></div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full text-sm" data-testid="cne-cmp-table">
            <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2.5">Región</th><th className="text-left px-4 py-2.5">Entidad</th><th className="text-left px-4 py-2.5">Municipio</th><th className="text-right px-4 py-2.5">$/kg anterior</th><th className="text-right px-4 py-2.5">$/kg actual</th><th className="text-right px-4 py-2.5">Var. $/kg</th><th className="text-right px-4 py-2.5">Var. %</th><th className="text-right px-4 py-2.5">$/litro actual</th><th className="text-right px-4 py-2.5">Var. $/litro</th></tr>
            </thead>
            <tbody>
              {lr ? <tr><td colSpan={9} className="py-10 text-center"><Loader2 className="w-5 h-5 text-red-600 animate-spin inline" /></td></tr> :
                shown.map((r, i) => (
                  <tr key={`${r.region}-${r.estado}-${r.municipio}-${i}`} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-semibold text-slate-700">{r.region}</td>
                    <td className="px-4 py-2 text-slate-600">{r.estado}</td>
                    <td className="px-4 py-2">{r.municipio}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-500">{money(r.kg_a)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(r.kg_b)}</td>
                    <td className="px-4 py-2 text-right tabular-nums"><Var v={r.var_kg} /></td>
                    <td className="px-4 py-2 text-right tabular-nums"><Var v={r.var_pct} suffix="%" /></td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(r.lt_b)}</td>
                    <td className="px-4 py-2 text-right tabular-nums"><Var v={r.var_lt} /></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100" data-testid="cne-cmp-count">{shown.length} registros</div>
      </div>
    </div>
  );
}
